import {inject, Injectable, OnDestroy} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {SafeArea} from '@aashu-dubey/capacitor-statusbar-safe-area';
import {Platform} from '@ionic/angular/standalone';
import {debounceTime, Subscription} from 'rxjs';
import {LoggerService} from "../logging/logger.service";

export interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export type LandscapeOrientation = 'left' | 'right' | null;

@Injectable({
  providedIn: 'root'
})
export class SafeAreaService implements OnDestroy {
  private safeAreaInsets: SafeAreaInsets = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  };

  private readonly logger = inject(LoggerService);
  private readonly platform = inject(Platform);

  private resizeSubscription: Subscription | null = null;
  private readonly DEBOUNCE_MS = 100;

  async initialise(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await this.updateSafeAreaInsets();
      this.setupResizeListener();
    }
  }

  ngOnDestroy(): void {
    this.resizeSubscription?.unsubscribe();
  }

  private setupResizeListener(): void {
    // Use Ionic's Platform.resize observable which fires on orientation changes
    this.resizeSubscription = this.platform.resize
      .pipe(debounceTime(this.DEBOUNCE_MS))
      .subscribe(() => {
        this.logger.debug('SafeAreaService: resize/orientation change detected');
        this.updateSafeAreaInsets();
      });

    this.logger.debug('SafeAreaService: resize listener set up');
  }

  private async updateSafeAreaInsets(): Promise<void> {
    try {
      const safeArea = await SafeArea.getSafeAreaInsets();
      this.safeAreaInsets = {
        top: safeArea.top,
        right: safeArea.right,
        bottom: safeArea.bottom,
        left: safeArea.left
      };

      this.logger.info('Safe area insets updated:', this.safeAreaInsets);
      this.applySafeAreaCSSProperties();
    } catch (error) {
      this.logger.warn('SafeAreaService: Failed to get safe area insets', error);
      // Fallback to default values
      this.safeAreaInsets = {top: 44, right: 0, bottom: 34, left: 0};
      this.applySafeAreaCSSProperties();
    }
  }

  private applySafeAreaCSSProperties(): void {
    const root = document.documentElement;
    this.logger.debug('Applying safe area CSS properties:', this.safeAreaInsets);

    root.style.setProperty('--ion-statusbar-padding', `${this.safeAreaInsets.top - 10}px`, 'important');
    root.style.setProperty('--ion-safe-area-top', `${this.safeAreaInsets.top - 10}px`, 'important');
    // The raw top inset: the height of the brand strip the light
    // scheme keeps under the status bar (the pinned desktop header
    // and the scrolling pages' content background, HEC-313). Only
    // ever set here, so on the web var(--hec-status-strip, 0px)
    // falls back to 0px.
    root.style.setProperty('--hec-status-strip', `${this.safeAreaInsets.top}px`, 'important');
    root.style.setProperty('--ion-safe-area-right', `${this.safeAreaInsets.right}px`, 'important');
    root.style.setProperty('--ion-safe-area-left', `${this.safeAreaInsets.left}px`, 'important');

    // On Android, the bottom margin is handled natively in MainActivity.java
    if (Capacitor.getPlatform() === 'android') {
      if (this.isLandscape()) {
        root.style.setProperty('--ion-safe-area-bottom', '0px', 'important');
      } else {
        if (this.getOrientationType()) {
          if (this.getOrientationType() === 'landscape-primary') {
            // home button on the right
            root.style.setProperty('--ion-safe-area-right', `0px`, 'important');
          } else {
            // home button on the left
            root.style.setProperty('--ion-safe-area-left', `0px`, 'important');
          }

        }
      }
    } else {
      root.style.setProperty('--ion-safe-area-bottom', `${this.safeAreaInsets.bottom - 10}px`, 'important');
    }
  }

  /**
   * Returns the current safe area insets
   */
  getInsets(): SafeAreaInsets {
    return {...this.safeAreaInsets};
  }

  /**
   * Returns true if the device is in landscape orientation
   */
  isLandscape(): boolean {
    return this.platform.isLandscape();
  }

  /**
   * In landscape mode, determines which side the notch/camera cutout is on.
   * Returns 'left' if the notch is on the left (device rotated clockwise),
   * 'right' if on the right (device rotated counter-clockwise),
   * or null if in portrait mode or no significant difference.
   */
  getLandscapeNotchSide(): LandscapeOrientation {
    if (!this.isLandscape()) {
      return null;
    }

    const {left, right} = this.safeAreaInsets;

    // If both are similar (within threshold), no clear notch side
    const threshold = 5;
    if (Math.abs(left - right) < threshold) {
      return null;
    }

    return left > right ? 'left' : 'right';
  }

  /**
   * Uses the Screen Orientation API to get the precise orientation type.
   * Returns: 'portrait-primary', 'portrait-secondary', 'landscape-primary', 'landscape-secondary'
   * or null if not available.
   */
  getOrientationType(): OrientationType | null {
    return window.screen?.orientation?.type ?? null;
  }
}
